/**
 * Client helper for Meta CAPI events (PageView, InitiateCheckout, Purchase).
 * Collects first-party fbc/fbp and posts to the server CAPI sender.
 * The access token never leaves the server. No Meta Pixel is loaded.
 */

const sentEventIds = new Set();

const getCapiEventUrl = () => {
  if (process.env.REACT_APP_META_CAPI_URL) {
    return process.env.REACT_APP_META_CAPI_URL;
  }
  return "/api/meta/event";
};

const readCookie = (name) => {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : "";
};

const writeCookie = (name, value, maxAgeSeconds) => {
  if (typeof document === "undefined" || !value) return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
};

const storageGet = (key) => {
  try {
    return sessionStorage.getItem(key) || "";
  } catch (err) {
    return "";
  }
};

const storageSet = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch (err) {
    // ignore
  }
};

export const getCapiSessionKey = () => {
  const existing = storageGet("rx_capi_session");
  if (existing) return existing;
  const created = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  storageSet("rx_capi_session", created);
  return created;
};

export const getFacebookCookies = () => ({
  fbc: readCookie("_fbc") || "",
  fbp: readCookie("_fbp") || "",
});

export const captureFacebookClickId = () => {
  if (typeof window === "undefined") return getFacebookCookies();

  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get("fbclid");
  const existingFbc = readCookie("_fbc");

  if (fbclid && !existingFbc) {
    const timestamp = Date.now();
    writeCookie("_fbc", `fb.1.${timestamp}.${fbclid}`, 90 * 24 * 60 * 60);
  }

  return getFacebookCookies();
};

export const resolvePurchaseEventId = (orderOrList) => {
  const order = Array.isArray(orderOrList) ? orderOrList[0] : orderOrList;
  if (!order || typeof order !== "object") return "";
  return (
    order.stripe_checkout_session_id ||
    order.stripe_session_id ||
    order.checkout_session_id ||
    order.session_id ||
    (order.order_id != null ? String(order.order_id) : "")
  );
};

export const sendMetaCapiEvent = async ({
  eventName,
  eventId,
  email,
  phone,
  value,
  eventTime,
}) => {
  const name = eventName || "Purchase";
  const id = eventId ? String(eventId) : "";
  if (!id) {
    console.warn(`[meta-capi] skipped ${name}: missing event_id`);
    return { success: false, skipped: true };
  }
  const dedupeKey = `${name}:${id}`;
  if (sentEventIds.has(dedupeKey)) {
    return { success: true, duplicate: true, event_id: id, event_name: name };
  }
  sentEventIds.add(dedupeKey);

  const { fbc, fbp } = captureFacebookClickId();
  const payload = {
    event_name: name,
    event_id: id,
    eventId: id,
    event_time: eventTime || Math.floor(Date.now() / 1000),
    email: email || "",
    phone: phone || "",
    fbc: fbc || undefined,
    fbp: fbp || undefined,
    client_user_agent:
      typeof navigator !== "undefined" ? navigator.userAgent : "",
  };
  if (name === "Purchase" || name === "InitiateCheckout") {
    payload.value = Number.parseFloat(value) || 0;
  }

  try {
    const response = await fetch(getCapiEventUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      console.error(`[meta-capi] ${name} failed`, result);
      sentEventIds.delete(dedupeKey);
      return { success: false, error: result.error || response.statusText, meta: result.meta };
    }
    console.log(`[meta-capi] ${name} accepted`, {
      event_id: result.event_id,
      events_received: result.events_received,
      fbtrace_id: result.fbtrace_id,
      duplicate: result.duplicate,
      test_event_code: result.test_event_code || null,
    });
    return result;
  } catch (error) {
    console.error(`[meta-capi] ${name} request error`, error);
    sentEventIds.delete(dedupeKey);
    return { success: false, error: error.message };
  }
};

export const sendMetaCapiPageView = ({ email, phone } = {}) => {
  const sessionKey = getCapiSessionKey();
  if (storageGet("rx_capi_pageview_sent")) {
    return Promise.resolve({ success: true, duplicate: true });
  }
  storageSet("rx_capi_pageview_sent", "1");
  return sendMetaCapiEvent({
    eventName: "PageView",
    eventId: `pv_${sessionKey}`,
    email,
    phone,
  });
};

export const sendMetaCapiInitiateCheckout = ({ email, phone, value } = {}) => {
  const sessionKey = getCapiSessionKey();
  if (storageGet("rx_capi_ic_sent")) {
    return Promise.resolve({ success: true, duplicate: true });
  }
  storageSet("rx_capi_ic_sent", "1");
  return sendMetaCapiEvent({
    eventName: "InitiateCheckout",
    eventId: `ic_${sessionKey}`,
    email,
    phone,
    value,
  });
};

export const sendMetaCapiPurchase = (params) =>
  sendMetaCapiEvent({
    eventName: "Purchase",
    ...params,
  });
